// PRD.316 — Clinical Findings + the canonical odontogram vocabulary.
//
// TWO DISTINCT RECORDS
// --------------------
//   Part Chart      = structured odontogram state (`odontogram_charts.tooth_states`)
//   Clinical Finding = clinician-confirmed encounter documentation
//
// They may be linked by provenance, but neither silently rewrites the other.
// A chart condition only ever produces a SUGGESTION; nothing is persisted as a
// finding until the clinician accepts it, and once accepted the narrative is
// ordinary documentation that later chart edits must never touch.
//
// WHY THE VOCABULARY LIVES HERE
// -----------------------------
// The tooth/surface/condition vocabulary was defined as literals inside
// `OdontogramChart.tsx` and `ToothConditionsModal.tsx`. Suggestions have to read
// the same values, and PRD §5/§7.3 forbids a second tooth, surface or condition
// model — so the canonical definitions moved here and the chart components now
// import them. Colors stay in the components: those are presentation, not
// clinical identity.
//
// This module is self-contained (no workspace-package imports) so it runs under
// the repo's node:test harness.

// ─── Canonical tooth-surface vocabulary ───

/**
 * Tooth surfaces, exactly as the Part Chart stores them inside
 * `tooth_states[tooth].conditions[].surfaces`. Full lowercase words are the
 * stored identity; the single letters are DISPLAY ONLY (see
 * `formatSurfaceInitials`) and are never persisted.
 */
export const TOOTH_SURFACES = ['occlusal', 'mesial', 'distal', 'buccal', 'lingual'] as const;

export type ToothSurface = (typeof TOOTH_SURFACES)[number];

export const TOOTH_SURFACE_LABELS: Record<ToothSurface, string> = {
  occlusal: 'Occlusal',
  mesial: 'Mesial',
  distal: 'Distal',
  buccal: 'Buccal',
  lingual: 'Lingual',
};

/** ADA-style single-letter surface codes, for compact chips like `16 · MO`. */
export const TOOTH_SURFACE_INITIALS: Record<ToothSurface, string> = {
  occlusal: 'O',
  mesial: 'M',
  distal: 'D',
  buccal: 'B',
  lingual: 'L',
};

/**
 * Whole-tooth flags. `ok` is the default healthy state and is deliberately NOT
 * a finding — suggesting "tooth 16 is fine" would be noise.
 */
export const TOOTH_FLAG_LABELS: Record<string, string> = {
  ok: 'OK',
  missing: 'Missing',
  root_fragment: 'Root Fragment',
  implant: 'Implant',
  impacted: 'Impacted',
};

/**
 * Surface-level conditions.
 *
 * The three cervical lesions are ordinary conditions in this model: they are
 * recorded per-surface exactly like caries, and adding them needs no schema
 * change because `tooth_states` is a JSON blob and `code` is free text. Adding
 * a code here ALSO makes it appear automatically in PRD.316's chart
 * suggestions — `buildChartSuggestions` looks the label up and skips codes it
 * does not recognise.
 *
 * Cervical lesions are anatomically at the gum line rather than on the five
 * charted surfaces, so they reuse the existing surface model (typically buccal
 * / lingual) rather than introducing a cervical coordinate system.
 */
export const TOOTH_CONDITION_LABELS: Record<string, string> = {
  caries: 'Caries',
  composite: 'Composite',
  amalgam: 'Amalgam',
  recurrent_caries: 'Recurrent Caries',
  cervical_abrasion: 'Cervical Abrasion',
  cervical_attrition: 'Cervical Attrition',
  cervical_abfraction: 'Cervical Abfraction',
};

/** Crown/restoration materials. */
export const TOOTH_CROWN_LABELS: Record<string, string> = {
  ceramic: 'Ceramic',
  pfm: 'PFM',
  gold: 'Gold',
  metal: 'Metal',
};

// ─── Canonical chart state shape ───

export interface ToothCondition {
  code: string;
  surfaces: string[];
}

/**
 * Tooth mobility grades (Miller-style).
 *
 * `'none'` is an EXPLICIT clinical statement that mobility was assessed and
 * found normal. It is NOT the same as the field being absent, which means
 * "not recorded" — see `readToothMobility`.
 */
export const TOOTH_MOBILITY_GRADES = ['none', 'i', 'ii', 'iii'] as const;

export type ToothMobility = (typeof TOOTH_MOBILITY_GRADES)[number];

export const TOOTH_MOBILITY_LABELS: Record<ToothMobility, string> = {
  none: 'None',
  i: 'I',
  ii: 'II',
  iii: 'III',
};

export interface ToothState {
  /** 'ok' | 'missing' | 'root_fragment' | 'implant' | 'impacted' */
  flags: string[];
  conditions: ToothCondition[];
  crown: string | null;
  /**
   * Tooth mobility — a structured periodontal finding, NOT a coloured
   * condition. Deliberately its own field rather than a `conditions[]` entry:
   * mobility is graded and tooth-level, so forcing it into the surface/colour
   * model would misrepresent it and would collide with the restorative
   * conditions it must coexist with.
   *
   * OPTIONAL and absent by default. An untouched historical tooth has no
   * `mobility` key and must never be backfilled to 'none' — that would
   * fabricate a clinical assessment nobody performed.
   *
   * Pocket depth is deliberately NOT here: proper periodontal charting needs
   * six site-specific measurements per tooth and gets its own design.
   */
  mobility?: string | null;
}

/** `odontogram_charts.tooth_states`, keyed by FDI tooth number as a string. */
export type ToothStatesMap = Record<string, ToothState>;

/**
 * Parse the chart's JSON blob defensively — a malformed or half-written blob
 * must degrade to "no chart data", never throw inside a render.
 */
export function parseToothStatesMap(raw: string | null | undefined): ToothStatesMap {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as ToothStatesMap;
  } catch {
    return {};
  }
}

// ─── Findings ───

/** One tooth's context on a finding. `surfaces` may be empty (whole tooth). */
export interface FindingTooth {
  /** FDI number as a string, matching the Part Chart's `tooth_states` keys. */
  tooth: string;
  surfaces: string[];
}

export interface ClinicalFindingRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  encounter_id: string | null;
  text: string | null;
  /** JSON array of `FindingTooth`, or null for a finding with no dental context. */
  teeth: string | null;
  /** 'manual' | 'chart' */
  source: string | null;
  /** Deterministic key of the originating chart entry — see `chartSuggestionRef`. */
  source_ref: string | null;
  /** Originating chart code (e.g. 'caries', 'implant'), for provenance chips. */
  condition_code: string | null;
  sort_order: number | string | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

/**
 * Read a finding's dental context. Returns [] for a general (no-tooth) finding
 * and for anything unparseable — a bad blob must not blank the narrative.
 */
export function parseFindingTeeth(raw: string | null | undefined): FindingTooth[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const out: FindingTooth[] = [];
    for (const entry of parsed) {
      if (!entry || typeof entry !== 'object') continue;
      const e = entry as { tooth?: unknown; surfaces?: unknown };
      const tooth = typeof e.tooth === 'string' ? e.tooth : String(e.tooth ?? '');
      if (!tooth) continue;
      const surfaces = Array.isArray(e.surfaces)
        ? e.surfaces.filter((s): s is string => typeof s === 'string')
        : [];
      out.push({ tooth, surfaces });
    }
    return out;
  } catch {
    return [];
  }
}

/** Serialize dental context. An empty list stores NULL, not `[]`, so a general
 *  finding is unambiguously "no dental context" at the database level. */
export function serializeFindingTeeth(teeth: readonly FindingTooth[]): string | null {
  if (teeth.length === 0) return null;
  return JSON.stringify(teeth.map((t) => ({ tooth: t.tooth, surfaces: t.surfaces })));
}

/** Compact surface code for a chip: `['mesial','occlusal']` → `'MO'`. */
export function formatSurfaceInitials(surfaces: readonly string[]): string {
  // Canonical order, not input order, so the same set always reads the same.
  return TOOTH_SURFACES.filter((s) => surfaces.includes(s))
    .map((s) => TOOTH_SURFACE_INITIALS[s])
    .join('');
}

/**
 * Compact dental context label: `16 · MO`, `31, 32, 41, 42`, or '' when the
 * finding is general.
 */
export function formatToothContext(teeth: readonly FindingTooth[]): string {
  if (teeth.length === 0) return '';
  if (teeth.length === 1) {
    const initials = formatSurfaceInitials(teeth[0].surfaces);
    return initials ? `${teeth[0].tooth} · ${initials}` : teeth[0].tooth;
  }
  return teeth.map((t) => t.tooth).join(', ');
}

/** Findings in stable display order: explicit `sort_order`, then creation time. */
export function sortFindings(rows: readonly ClinicalFindingRow[]): ClinicalFindingRow[] {
  return [...rows].sort((a, b) => {
    const ao = Number(a.sort_order ?? 0);
    const bo = Number(b.sort_order ?? 0);
    if (ao !== bo) return ao - bo;
    return (a.created_at ?? '').localeCompare(b.created_at ?? '');
  });
}

// ─── Chart → suggestions ───

/** What kind of chart entry produced a suggestion. */
export type ChartSuggestionKind = 'flag' | 'condition' | 'crown' | 'mobility';

export interface ChartSuggestion {
  /**
   * Deterministic identity of the originating chart entry. Stored on an
   * accepted finding as `source_ref` and used to suppress re-suggestion.
   */
  ref: string;
  chartId: string;
  tooth: string;
  kind: ChartSuggestionKind;
  /** Chart code, e.g. 'caries' / 'implant' / 'pfm'. */
  code: string;
  /** Presentation label, e.g. 'Caries'. */
  label: string;
  surfaces: string[];
}

/**
 * Deterministic key for one chart entry.
 *
 * The Part Chart stores tooth state as a JSON blob, so individual entries have
 * NO database id to reference (PRD §8.4 assumes one may exist — it does not).
 * This composite is the stable substitute: the chart row's own UUID plus the
 * tooth, entry kind and code. It survives narrative edits, unrelated chart
 * edits and re-renders, which text equality would not.
 */
export function chartSuggestionRef(
  chartId: string,
  tooth: string,
  kind: ChartSuggestionKind,
  code: string,
): string {
  return `${chartId}:${tooth}:${kind}:${code}`;
}

/**
 * Derive suggestions from ONE chart's tooth states.
 *
 * Scope is deliberately narrow (PRD §8.5): callers pass only the ACTIVE
 * encounter's chart, so a patient's entire charting history is never dumped
 * into the current visit as suggestions.
 *
 * `ok` flags are skipped — a healthy tooth is not a finding. Teeth are emitted
 * in ascending FDI order so the strip is stable between renders.
 */
export function buildChartSuggestions(
  chartId: string,
  toothStates: ToothStatesMap,
): ChartSuggestion[] {
  const out: ChartSuggestion[] = [];
  const teeth = Object.keys(toothStates).sort((a, b) => Number(a) - Number(b));

  for (const tooth of teeth) {
    const state = toothStates[tooth];
    if (!state || typeof state !== 'object') continue;

    for (const flag of Array.isArray(state.flags) ? state.flags : []) {
      if (flag === 'ok') continue;
      const label = TOOTH_FLAG_LABELS[flag];
      if (!label) continue;
      out.push({
        ref: chartSuggestionRef(chartId, tooth, 'flag', flag),
        chartId,
        tooth,
        kind: 'flag',
        code: flag,
        label,
        surfaces: [],
      });
    }

    for (const cond of Array.isArray(state.conditions) ? state.conditions : []) {
      if (!cond || typeof cond !== 'object') continue;
      const label = TOOTH_CONDITION_LABELS[cond.code];
      if (!label) continue;
      out.push({
        ref: chartSuggestionRef(chartId, tooth, 'condition', cond.code),
        chartId,
        tooth,
        kind: 'condition',
        code: cond.code,
        label,
        surfaces: Array.isArray(cond.surfaces) ? cond.surfaces : [],
      });
    }

    // Mobility is a structured finding, so it gets its own suggestion kind
    // rather than masquerading as a condition. 'none' is still offered: "this
    // tooth was assessed and is firm" is legitimate documentation.
    const mobility = readToothMobility(state);
    if (mobility) {
      out.push({
        ref: chartSuggestionRef(chartId, tooth, 'mobility', mobility),
        chartId,
        tooth,
        kind: 'mobility',
        code: mobility,
        label: `Mobility ${TOOTH_MOBILITY_LABELS[mobility]}`,
        surfaces: [],
      });
    }

    if (state.crown) {
      const label = TOOTH_CROWN_LABELS[state.crown];
      if (label) {
        out.push({
          ref: chartSuggestionRef(chartId, tooth, 'crown', state.crown),
          chartId,
          tooth,
          kind: 'crown',
          code: state.crown,
          label: `${label} Crown`,
          surfaces: [],
        });
      }
    }
  }

  return out;
}

/**
 * Drop suggestions already accepted into this encounter (PRD §8.4).
 *
 * Matches on `source_ref`, never on narrative text: the clinician is expected
 * to rewrite the seeded narrative, and text equality would resurrect every
 * edited finding as a duplicate suggestion.
 */
export function filterAcceptedSuggestions(
  suggestions: readonly ChartSuggestion[],
  findings: readonly ClinicalFindingRow[],
): ChartSuggestion[] {
  const accepted = new Set(
    findings.map((f) => f.source_ref).filter((r): r is string => typeof r === 'string' && r !== ''),
  );
  return suggestions.filter((s) => !accepted.has(s.ref));
}

/** Compact chip label for a suggestion: `16 · Caries · MO`. */
export function formatSuggestionLabel(s: ChartSuggestion): string {
  const initials = formatSurfaceInitials(s.surfaces);
  return [s.tooth, s.label, initials || null].filter(Boolean).join(' · ');
}

/** The values a newly accepted suggestion is persisted with. */
export interface FindingDraft {
  text: string;
  teeth: FindingTooth[];
  source: 'chart';
  sourceRef: string;
  conditionCode: string;
}

/**
 * Turn an accepted suggestion into a finding's initial values (PRD §8.2).
 *
 * The narrative seed is the bare condition label — a starting point the
 * clinician replaces ("Deep carious lesion involving..."), not a generated
 * sentence pretending to be documentation. Tooth, surface and condition context
 * ride alongside as structured metadata rather than being baked into the text,
 * so editing the narrative never destroys the provenance.
 */
export function suggestionToFindingDraft(s: ChartSuggestion): FindingDraft {
  return {
    text: s.label,
    teeth: [{ tooth: s.tooth, surfaces: s.surfaces }],
    source: 'chart',
    sourceRef: s.ref,
    conditionCode: s.code,
  };
}

// ─── Narrative field sizing ───

export const FINDING_TEXTAREA_MIN_ROWS = 1;

/** Beyond this the field scrolls internally rather than pushing the list down. */
export const FINDING_TEXTAREA_MAX_ROWS = 4;

/**
 * Visible rows for a finding's narrative field.
 *
 * Counts EXPLICIT newline characters only — never character count and never
 * rendered wrapping. A finding is a compact row in a list that may hold dozens
 * of entries, so height must represent deliberate structure the clinician typed
 * (paragraphs, one-per-line lists), not the accident of how wide the column
 * happens to be. A long single sentence that merely wraps stays one row; every
 * Enter adds one, and deleting newlines shrinks it back.
 *
 * `''.split('\n')` is `['']`, so an empty value naturally yields the minimum.
 * A trailing newline counts as the line the caret is now on, which is what a
 * clinician who just pressed Enter expects to see.
 */
export function getFindingTextareaRows(
  value: string | null | undefined,
  maxRows: number = FINDING_TEXTAREA_MAX_ROWS,
): number {
  const lines = (value ?? '').split('\n').length;
  const ceiling = Math.max(FINDING_TEXTAREA_MIN_ROWS, maxRows);
  return Math.min(Math.max(lines, FINDING_TEXTAREA_MIN_ROWS), ceiling);
}

// ─── Tooth mobility ───

/**
 * Read a tooth's recorded mobility.
 *
 * Returns null for "not recorded" — an absent field, an empty string, or a
 * value this build does not recognise. `'none'` is returned as a real grade,
 * because a clinician stating that mobility is normal is a finding, not the
 * absence of one.
 */
export function readToothMobility(state: ToothState | null | undefined): ToothMobility | null {
  const raw = (state?.mobility ?? '').toString().trim().toLowerCase();
  if (!raw) return null;
  return (TOOTH_MOBILITY_GRADES as readonly string[]).includes(raw) ? (raw as ToothMobility) : null;
}

/** Display label, e.g. `Mobility II`. Empty when nothing was recorded. */
export function formatToothMobility(state: ToothState | null | undefined): string {
  const grade = readToothMobility(state);
  if (!grade) return '';
  return `Mobility ${TOOTH_MOBILITY_LABELS[grade]}`;
}
